import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  provideEnvironmentInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter, withRouterConfig } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { provideChatBot } from '@coin/chat-bot-feature';
import { AuthUserState } from '@coin/shared/auth/data-access';
import { AuthComponentsModule, AuthGmsAngularClientModule, UserState } from '@coin/shared/auth/feature';
import { AuthGmsClientConfig } from '@coin/shared/auth/util';
import { ApiModule as ApiModuleV2, Configuration as ConfigurationV2 } from '@coin/shared/core/data-access-v2';
import { provideInjectablePipes } from '@coin/shared/core/feature-pipes';
import { environment } from '@coin/shared/core/util-environments';
import { provideDefaultDateFormats } from '@coin/shared/core/util-helpers';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from 'ngx-toastr';
import { ApiModule as CosmosApiModule, Configuration as CosmosConfiguration } from './api/generated';
import { APP_ROUTES } from './app.routes';
import { httpInterceptorProviders } from './http-interceptors';

const iconUrls: Record<string, string> = {
  help_icon: '../../assets/svgs/help.svg',
  cylinder: '../../assets/svgs/org-management/cylinder.svg',
  bee: '../../assets/svgs/org-management/bee.svg',
  co_purple: '../../assets/svgs/co-purple.svg',
  ms_teams: '../../assets/svgs/ms-teams.svg',
  org: '../../assets/svgs/org.svg',
  share: '../../assets/svgs/share.svg',
  sort: '../../assets/svgs/legal-entities/sort.svg'
};

function loadIcons(): void {
  const matIconRegistry = inject(MatIconRegistry);
  const domSanitizer = inject(DomSanitizer);
  Object.entries(iconUrls).forEach(([name, url]) =>
    matIconRegistry.addSvgIcon(name, domSanitizer.bypassSecurityTrustResourceUrl(url))
  );
}

export const APP_CONFIG: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(APP_ROUTES, withRouterConfig({ onSameUrlNavigation: 'reload' })),
    importProvidersFrom(
      BrowserModule,
      ToastrModule.forRoot(),
      AuthGmsAngularClientModule.forRoot({
        ...environment.authentication,
        ...environment.cosmosAuthentication
      } as AuthGmsClientConfig),
      TranslateModule.forRoot({
        loader: provideTranslateHttpLoader({
          prefix: `${environment.cmsContentProdBucketCloudfront}translations/cosmos/`,
          suffix: '.json'
        })
      }),
      MatIconModule,
      MatDialogModule,
      BrowserAnimationsModule,
      ApiModuleV2.forRoot(() => new ConfigurationV2({ basePath: environment.api.baseUrl.replace('/cosmosapi', '') })),
      CosmosApiModule.forRoot(() => new CosmosConfiguration({ basePath: environment.api.baseUrl })),
      MatBadgeModule,
      AuthComponentsModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: !isDevMode(),
        registrationStrategy: 'registerWithDelay:5000'
      })
    ),
    { provide: AuthUserState, useExisting: UserState },
    httpInterceptorProviders,
    TranslateService,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideInjectablePipes(),
    provideDefaultDateFormats(),
    provideChatBot(),
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } }, // fix for https://jira.siemens-energy.com/browse/GMTEC-22822
    provideEnvironmentInitializer(loadIcons)
  ]
};
